import React, {useCallback, useEffect, useState} from 'react';
import {Box, Checkbox, Stack} from '@mui/material';
import PropTypes from 'prop-types';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const CheckboxQuestion = ({
                              componentId,
                              descriptionHtml,
                              onDescriptionHtmlChange,
                              commitDescriptionHtml,
                              value,
                              onChange,
                              isEditMode,
                              enableValidation,
                              isFormArchived,
                              readOnly,
                              placeholderValues,
                              accessLevel,
                              error,
                              audience,
                              recipientType,
                              linkedToStudents,
                              submitMode,
                              index
                          }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            gap={3}
                        >
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived}
                            />
                        </Stack>
                    </>
                )
                : (
                    <FormControl
                        key={`${descriptionHtml}-${value}`}
                        error={!!error}
                        variant="standard"
                        fullWidth
                    >
                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={2}
                        >
                            <RichEditorResultText
                                id={componentId}
                                description={descriptionHtml}
                                placeholderValues={placeholderValues}
                                readonly={readOnly}
                                enableValidation={enableValidation}
                                error={!!error}
                                audience={audience}
                                recipientType={recipientType}
                                linkedToStudents={linkedToStudents}
                                submitMode={submitMode}
                            />
                            <Checkbox
                                checked={!!value}
                                disabled={readOnly}
                                onChange={e =>
                                    onChange({
                                        id: componentId,
                                        value: e.target.checked,
                                    })
                                }
                            />
                        </Stack>
                        <FormHelperText>{error}</FormHelperText>
                    </FormControl>
                )}
        </Box>
    );
};

CheckboxQuestion.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    value: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool,
    index: PropTypes.number,
};

export default CheckboxQuestion;